import { BaseEdgeFunctionClient } from '../shared/base-client';
import { 
  TextGenerationRequest, 
  TextGenerationResponse, 
  TextAssistant, 
  TextPrompt,
  OpenAIConfig 
} from './types';

export class OpenAITextGenerationClient extends BaseEdgeFunctionClient {
  constructor(config: OpenAIConfig = {}) {
    super(config);
  }

  /**
   * Generate text using OpenAI
   */
  async generateText(request: TextGenerationRequest): Promise<string> {
    try {
      const response = await this.invokeEdgeFunction<TextGenerationResponse>(
        'text-generation',
        {
          ...request,
          appName: request.appName || 'Text Generation',
          functionName: request.functionName || request.type || 'Basic Generation'
        }
      );

      if (!response.success) {
        throw new Error(response.error || 'Text generation failed');
      }

      return response.generatedText || '';
    } catch (error) {
      this.handleError(error, 'Text generation failed');
    }
  }

  /**
   * Get all available text generation assistants
   */
  async getAssistants(): Promise<TextAssistant[]> {
    try {
      const response = await this.invokeEdgeFunction<{ assistants: TextAssistant[] }>(
        'text-generation',
        { type: 'assistants' }
      );

      return response.assistants || [];
    } catch (error) {
      this.handleError(error, 'Failed to fetch assistants');
    }
  }

  /**
   * Get all prompts or prompts by category
   */
  async getPrompts(category?: string): Promise<TextPrompt[]> {
    try {
      const response = await this.invokeEdgeFunction<{ prompts: TextPrompt[] }>(
        'text-generation',
        { type: 'prompts', category }
      );

      return response.prompts || [];
    } catch (error) {
      this.handleError(error, 'Failed to fetch prompts');
    }
  }

  /**
   * Get prompts by specific category
   */
  async getPromptsByCategory(category: string): Promise<TextPrompt[]> {
    return this.getPrompts(category);
  }

  /**
   * Generate text with specific assistant
   */
  async generateWithAssistant(
    message: string, 
    assistantId: string, 
    options: { maxTokens?: number; temperature?: number } = {}
  ): Promise<string> {
    return this.generateText({
      message,
      assistantId,
      ...options
    });
  }
} 